import React, { useState, useMemo } from 'react';
import * as DTO from '../../../dto';
import {
  Popover,
  ListItem,
  List,
  ListItemText,
  Collapse,
  Icon,
  Typography,
  createStyles,
  makeStyles,
  Snackbar,
  IconButton,
  Slide,
  ListSubheader
} from '@material-ui/core';
import { LanguageString } from '../../Common';
import { Alert } from '@material-ui/lab';
import { CancelRounded } from '@material-ui/icons';
import { TransitionProps } from '@material-ui/core/transitions';

// Helper function to convert role enum to display name
const getRoleDisplayName = (role?: DTO.Role | null): string | null => {
  if (!role) return null;

  switch (role) {
    case DTO.Role.SUPERADMIN: return 'Super Admin';
    case DTO.Role.ADMIN: return 'Admin';
    case DTO.Role.EDITOR: return 'Editor';
    case DTO.Role.REPORTER: return 'Reporter';
    case DTO.Role.ADVISOR: return 'Advisor';
    case DTO.Role.STUDENT: return 'Student';
    case DTO.Role.EMPLOYER: return 'Employer';
    default: return null;
  }
};

interface MTSActivityDropDownProps {
  activityTypes: DTO.MTSActivityType[];
  assignedActivityTypeIds: number[];
  anchor: React.RefObject<HTMLButtonElement>;
  open: boolean;
  toggle: () => void;
  selectedActivityType: (type: DTO.MTSActivityType | DTO.UserMTSActivityType) => void;
  compatibilityMode?: boolean;
  existingActivityType?: DTO.MTSActivityType | DTO.UserMTSActivityType;
}

interface MTSActivityDropDownLineProps {
  type: DTO.MTSActivityType;
  selectedType: (type: DTO.MTSActivityType | DTO.UserMTSActivityType) => void;
  compatibilityMode?: boolean;
  existingActivityType?: DTO.MTSActivityType | DTO.UserMTSActivityType;
  setErrorMsg: (msg: string) => void;
}

const useStyles = makeStyles(theme =>
  createStyles({
    expiredText: {
      fontStyle: 'italic',
      color: theme.palette.error.main
    },
    compatibleText: {
      fontStyle: 'normal',
      color: theme.palette.text.primary
    }
  })
);

function TransitionUp(props: TransitionProps & { children?: React.ReactElement }) {
  return <Slide {...props} direction="up" />;
}

interface MTSActivityDropDownLineChildProps {
  ct: DTO.UserMTSActivityType;
  selectedType: (type: DTO.MTSActivityType | DTO.UserMTSActivityType) => void;
  compatibilityMode?: boolean;
  existingActivityType?: DTO.MTSActivityType | DTO.UserMTSActivityType;
  setErrorMsg: (msg: string) => void;
}

const MTSActivityDropDownLineChild: React.FC<MTSActivityDropDownLineChildProps> = ({ ct, selectedType, compatibilityMode, existingActivityType, setErrorMsg }) => {

  const classes = useStyles();
  const parentActivityTypeCompatible = useMemo(() => {
    if (existingActivityType && compatibilityMode) {
      const parentActivityType = ct.parentActivityType;
      if (parentActivityType) {
        const existingParentActivityType = 'expiring' in existingActivityType ? existingActivityType.parentActivityType : existingActivityType;
        return parentActivityType.formId === existingParentActivityType.formId;
      }
    }

    return false;
  }, [existingActivityType, compatibilityMode, ct]);
  const itemTextStyle = useMemo(() =>
    compatibilityMode === true ? (parentActivityTypeCompatible ? classes.compatibleText : classes.expiredText) : classes.compatibleText,
    [compatibilityMode, parentActivityTypeCompatible, classes.compatibleText, classes.expiredText]
  );
  const canSelectType = useMemo(() => {
    if (compatibilityMode) {
      return parentActivityTypeCompatible;
    }
    return true;
  }, [compatibilityMode, parentActivityTypeCompatible]);

  return (
    <ListItem
      key={`child_activity_${ct.id}`}
      button
      onClick={(event) => {
        if (canSelectType) {
          selectedType(ct);
        } else {
          setErrorMsg('This is an incompatible activity type.');
        }
      }}
      style={{ paddingLeft: 40 }}
    >
      <ListItemText
        className={itemTextStyle}
        primary={
          <Typography style={{ fontStyle: ct.expiring ? 'italic' : 'normal' }}>
            <LanguageString languageString={ct.name} />
          </Typography>
        }
        secondary={(
          <Typography style={{ fontStyle: 'italic', fontSize: 11 }}>
            {ct.parentActivityType?.role ? `${getRoleDisplayName(ct.parentActivityType.role)} activity type` : undefined}
          </Typography>
        )}
      />
    </ListItem>
  );
};

const MTSActivityDropDownLine: React.FC<MTSActivityDropDownLineProps> = ({ type, selectedType, compatibilityMode, existingActivityType, setErrorMsg }) => {

  const classes = useStyles();

  const [expansionOpen, setExpansionOpen] = useState(false);
  const parentActivityTypeCompatible = useMemo(() => {
    if (existingActivityType && compatibilityMode) {
      const parentActivityType = 'expiring' in existingActivityType ? existingActivityType.parentActivityType : existingActivityType;
      if (parentActivityType) {
        return parentActivityType.formId === type.formId;
      }
    }

    return false;
  }, [existingActivityType, compatibilityMode, type]);
  const itemTextStyle = useMemo(() =>
    compatibilityMode === true ? (parentActivityTypeCompatible ? classes.compatibleText : classes.expiredText) : classes.compatibleText,
    [compatibilityMode, parentActivityTypeCompatible, classes.compatibleText, classes.expiredText]
  );
  const canSelectType = useMemo(() => {
    if (compatibilityMode) {
      return parentActivityTypeCompatible;
    }
    return true;
  }, [compatibilityMode, parentActivityTypeCompatible]);

  const parentActivityType = existingActivityType ? ('expiring' in existingActivityType ? existingActivityType.parentActivityType : existingActivityType) : null;
  console.log(`formId ${type.formId} existingActivityType.formId ${parentActivityType?.formId}`);

  if (type.generatedChildTypes && type.generatedChildTypes.length > 0) {
    return (
      <>
        <ListItem
          button
          onClick={(event) => {
            setExpansionOpen(!expansionOpen);
          }}
        >
          <ListItemText
            className={itemTextStyle}
            style={{ fontStyle: 'italic', fontSize: 11 }}
            primary={<LanguageString languageString={type.name} />}
            secondary={(
              <Typography style={{ fontStyle: 'italic', fontSize: 11 }}>
                {type.role ? `${getRoleDisplayName(type.role)} activity type` : undefined}
              </Typography>
            )}
          />
          {expansionOpen ? <Icon>expand_less</Icon> : <Icon>expand_more</Icon>}
        </ListItem>
        <Collapse in={expansionOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {!type.hidden && (
              <ListItem
                key={`child_activity_${type.id}`}
                button
                onClick={(event) => {
                  if (canSelectType) {
                    selectedType(type);
                  } else {
                    setErrorMsg('This is an incompatible activity type.');
                  }
                }}
                style={{ paddingLeft: 40 }}
              >
                <ListItemText
                  primary={<LanguageString languageString={type.name} />}
                  secondary={(
                    <Typography style={{ fontStyle: 'italic', fontSize: 11 }}>
                      {type.role ? `${getRoleDisplayName(type.role)} activity type` : undefined}
                    </Typography>
                  )}
                />
              </ListItem>
            )}
            {type.generatedChildTypes.map(ct => (
              <MTSActivityDropDownLineChild
                ct={ct}
                selectedType={selectedType}
                compatibilityMode={compatibilityMode}
                existingActivityType={existingActivityType}
                setErrorMsg={setErrorMsg}
              />
            ))}
          </List>
        </Collapse>
      </>
    );
  } else {
    return (
      <ListItem
        button
        onClick={(event) => {
          if (canSelectType) {
            selectedType(type);
          } else {
            setErrorMsg('This is an incompatible activity type.');
          }
        }}
      >
        <ListItemText
          className={itemTextStyle}
          primary={<LanguageString languageString={type.name} />}
          secondary={(
            <Typography style={{ fontStyle: 'italic', fontSize: 11 }}>
              {type.role ? `${getRoleDisplayName(type.role)} activity type` : undefined}
            </Typography>
          )}
        />
      </ListItem>
    );
  }
};

export const MTSActivityDropDown: React.FC<MTSActivityDropDownProps> = (props) => {

  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  return (
    <>
      <Popover
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        anchorEl={props.anchor.current}
        open={props.open}
        onClose={props.toggle}
      >
        <List>
          {props.assignedActivityTypeIds.length > 1 &&
          <>
            <ListSubheader style={{ background: 'white' }}>Assigned Activities</ListSubheader>
            {props.activityTypes.filter(t => props.assignedActivityTypeIds.includes(t.id)).map(type => (
              <MTSActivityDropDownLine
                key={`assigned_activity_${type.id}`}
                type={type}
                selectedType={(selectedType) => {
                  props.toggle();
                  props.selectedActivityType(selectedType);
                }}
                compatibilityMode={props.compatibilityMode}
                existingActivityType={props.existingActivityType}
                setErrorMsg={setErrorMsg}
              />
            ))}
            <ListSubheader style={{ background: 'white' }}>All Activities</ListSubheader>
          </>
          }
          {props.activityTypes.filter(t => !t.hidden || (t.hidden && t.generatedChildTypes && t.generatedChildTypes.length > 0)).map(type => (
            <MTSActivityDropDownLine
              key={`activity_${type.id}`}
              type={type}
              selectedType={(selectedType) => {
                props.toggle();
                props.selectedActivityType(selectedType);
              }}
              compatibilityMode={props.compatibilityMode}
              existingActivityType={props.existingActivityType}
              setErrorMsg={setErrorMsg}
            />
          ))}
        </List>
      </Popover>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={errorMsg != null}
        TransitionComponent={TransitionUp}
        onClose={() => setErrorMsg(null)}
        key={`snackbar_error`}
        autoHideDuration={7000}
      >
        <Alert
          severity="error"
          action={
            <IconButton onClick={() => setErrorMsg(null)}><CancelRounded /></IconButton>
          }
        >
          <span onClick={() => setErrorMsg(null)}>
            {errorMsg}
          </span>
        </Alert>
      </Snackbar>
    </>
  );
};

export default MTSActivityDropDown;
